import axios, { AxiosRequestConfig } from 'axios';
import { Notification } from '../../types/sharedThesaurus';

const baseUrl = '/api/notifications';

// Fonction pour créer la configuration avec authentification
const createAuthConfig = (token?: string): AxiosRequestConfig => {
    if (!token) return {};
    return {
        headers: {
            'Authorization': `Bearer ${token}`,
            'accesstoken': token,
            'Content-Type': 'application/json'
        }
    };
};

// Variable pour stocker le token actuel
let currentToken: string | null = null;

// Fonction pour définir le token
export const setNotificationAuthToken = (token: string | null) => {
    currentToken = token;
};

/**
 * Service API pour les notifications
 */
export const notificationService = {
    /**
     * Récupère les notifications de l'utilisateur
     */
    getAll: async (options?: {
        page?: number;
        limit?: number;
        /** Filtre lu / non lu (le serveur attend `isRead=true|false`). */
        isRead?: boolean;
        /** Un type, ou plusieurs séparés par des virgules. */
        type?: string;
    }): Promise<{
        data: Notification[];
        unreadCount: number;
        pagination: { page: number; totalPages: number; total: number; hasNext: boolean };
    }> => {
        const params: Record<string, string | number> = {};
        if (options?.page) params.page = options.page;
        if (options?.limit) params.limit = options.limit;
        if (typeof options?.isRead === 'boolean') params.isRead = String(options.isRead);
        if (options?.type) params.type = options.type;
        const response = await axios.get(baseUrl, { params, ...createAuthConfig(currentToken || undefined) });
        const body = response.data;
        // Le serveur nomme la clé `pages` : on l'aligne sur le nom attendu par le store.
        return {
            data: body.data ?? [],
            unreadCount: body.unreadCount ?? 0,
            pagination: {
                page: body.pagination?.page ?? 1,
                totalPages: body.pagination?.pages ?? 1,
                total: body.pagination?.total ?? 0,
                hasNext: !!body.pagination?.hasNext,
            },
        };
    },

    /**
     * Récupère le nombre de notifications non lues
     */
    getUnreadCount: async (): Promise<number> => {
        const response = await axios.get(`${baseUrl}/unread-count`, 
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data.unreadCount;
    },

    /**
     * Marque une notification comme lue
     */
    markAsRead: async (notificationId: string): Promise<Notification> => {
        const response = await axios.put(
            `${baseUrl}/${notificationId}/read`,
            {},
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Marque toutes les notifications comme lues
     */
    markAllAsRead: async (): Promise<{ modifiedCount: number }> => {
        const response = await axios.put(
            `${baseUrl}/read-all`,
            {},
            createAuthConfig(currentToken || undefined)
        );
        return response.data.data;
    },

    /**
     * Supprime une notification
     */
    delete: async (notificationId: string): Promise<void> => {
        await axios.delete(
            `${baseUrl}/${notificationId}`,
            createAuthConfig(currentToken || undefined)
        );
    }
};
